// TeeTimeHelperAlternative.js
// Comparison: Tee Time Helper vs Tee Time Organizer.
// Framing: established, basic scheduler vs feature-rich golf platform, from a
// founder who subscribed to Tee Time Helper first.
//
// ACCURACY: competitor claims are sourced from Tee Time Helper's published site
// and dated. Games/settlement is NOT claimed as live (in development). League
// standings is NOT claimed (single-date scoring is live; standings on roadmap).
// Re-verify competitor pricing/features before each quarterly review.

import React from 'react';
import {
  H2,
  P,
  Callout,
  NoteBox,
  ComparisonTable,
  CompareCTA,
  FAQ,
  AttributionFooter,
} from '../compareUI';
import { colors } from '../compareData';

const REVIEWED = 'June 2026';

const FAQS = [
  {
    q: 'Is Tee Time Organizer harder to use than Tee Time Helper?',
    a: 'It does more, but the extra features stay out of the way until you need them. Scheduling a round and tracking RSVPs is just as quick; scoring, handicaps, and weather are there when you turn to them.',
  },
  {
    q: 'Can players RSVP without downloading an app?',
    a: 'Yes. Invites go out by email or text with a link, and players respond from the web — no app install required.',
  },
  {
    q: 'Does Tee Time Organizer sync official GHIN handicaps?',
    a: 'Yes. It connects to GHIN to keep handicaps current automatically, which is one of the main reasons organizers who care about official indexes switch.',
  },
  {
    q: 'I only need to schedule rounds. Is it worth switching?',
    a: 'If scheduling and RSVPs are all you need, both tools do that well and you may be happy where you are. Tee Time Organizer is the better fit when you also want GHIN handicaps, weather, scoring, or league tools in one place.',
  },
];

export default function TeeTimeHelperAlternative() {
  return (
    <article>
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: 12,
          fontWeight: 700,
          color: colors.green700,
          margin: '0 0 14px',
        }}
      >
        Golf group software · Comparison
      </p>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 34,
          fontWeight: 400,
          color: colors.gray900,
          lineHeight: 1.18,
          margin: '0 0 16px',
        }}
      >
        Tee Time Helper alternative: how Tee Time Organizer compares
      </h1>
      <p style={{ fontSize: 19, color: colors.gray600, lineHeight: 1.5, margin: '0 0 14px' }}>
        Tee Time Helper is a solid, established way to schedule rounds and track RSVPs. If your group
        also wants official handicaps, weather, scoring, or a season-long league, here's where a
        feature-rich platform fits.
      </p>
      <p
        style={{
          fontSize: 13,
          color: colors.gray500,
          borderBottom: `1px solid ${colors.gray200}`,
          paddingBottom: 22,
          margin: '0 0 30px',
        }}
      >
        Last reviewed {REVIEWED} · Comparison based on each product's publicly available information
      </p>

      <P>
        Tee Time Helper has been organizing golf groups for years and has earned a real following —
        tens of thousands of events created, mobile apps on both platforms, and a dependable handle
        on the core job of getting players signed up for a round. If you're using it today, or
        deciding between the two, this is a straight comparison: where they overlap, where they
        diverge, and which kind of group each one suits.
      </P>

      <NoteBox label="A note from the founder">
        Before I built Tee Time Organizer, I was a Tee Time Helper subscriber myself for over a year.
        It organized my group reliably, and I have no complaints about that. What I kept wanting was
        more — a richer interface, and tools for the golf side of running a group: official GHIN
        handicaps, weather, scoring, and eventually a season leaderboard. Not finding all of that in
        one place is exactly why I built Tee Time Organizer. So this comparison comes from someone
        who genuinely used and appreciated the other product first.
      </NoteBox>

      <H2>The short version</H2>
      <Callout>
        <strong style={{ color: colors.green800 }}>Tee Time Helper</strong> is a mature, dependable
        scheduling and RSVP tool with a deliberately simple, basic interface, and it's good at
        exactly that.
        <br />
        <br />
        <strong style={{ color: colors.green800 }}>Tee Time Organizer</strong> covers the same
        scheduling and RSVP job, then adds the golf-specific layer in one platform: pairings and
        flights, mobile scoring, automatic GHIN handicap sync, and weather on every tee time.
      </Callout>
      <P>
        The two are priced similarly, so this isn't a budget decision. It comes down to how much of
        your group's golf you want to run in one place. If you only schedule and chase RSVPs, Tee
        Time Helper does that well. If you also want official handicaps, weather, or scoring, that's
        where the feature gap opens up.
      </P>

      <H2>Feature comparison</H2>
      <ComparisonTable
        caption={`Based on the published Tee Time Helper website and apps as of ${REVIEWED}. Tee Time Helper is an active product and may add features over time — we re-check this page quarterly.`}
        columns={['What you need', 'Tee Time Helper', 'Tee Time Organizer']}
        rows={[
          ['Event scheduling & invitations', 'Yes', 'Yes'],
          ['One-click RSVP tracking', 'Yes', 'Yes'],
          ['Auto-promoting waitlist', 'Yes', 'Yes'],
          ['Mobile apps', 'Yes (iOS & Android)', 'Yes (mobile web)'],
          ['Pairings & balanced flights', 'Not listed', 'Yes'],
          ['Mobile scoring', 'Not listed', 'Yes (single-round)'],
          ['GHIN handicap sync', 'Not listed', 'Yes (automatic)'],
          ['Weather on each tee time', 'Not listed', 'Yes'],
          ['SMS + email reminders', 'Push + email', 'SMS + email'],
          ['Games & settlement', 'Not listed', 'Expanding — more formats coming'],
          ['Season standings & league play', 'Not listed', 'On the roadmap'],
        ]}
      />
      <p style={{ fontSize: 14, color: colors.gray500, lineHeight: 1.6, margin: '0 0 18px' }}>
        "Not listed" means the feature does not appear on the product's public website or app
        listings as of the review date — not that it's impossible. We describe differences in focus
        rather than guessing at what either app does internally.
      </p>

      <H2>Where Tee Time Helper fits</H2>
      <P>
        If your group's needs begin and end with scheduling and RSVPs, Tee Time Helper does that job
        well and has for years. It's established, stable, and its simplicity is a feature for
        organizers who want nothing more than to put a round on the calendar and see who's in. There
        is nothing wrong with picking the tool that does exactly what you need and no more.
      </P>

      <H2>Where organizers move to Tee Time Organizer</H2>
      <P>
        The move usually happens when an organizer realizes the scheduling is handled but everything
        else is still manual. Handicaps get looked up separately. Nobody knows the forecast until
        they're standing on the first tee. Scores live in a notebook. At that point you're running a
        scheduler plus several other tools.
      </P>
      <P>
        Tee Time Organizer brings the golf side into the same platform. The same tool that schedules
        the round and tracks RSVPs also builds the pairings, syncs official handicaps through GHIN,
        shows the weather for each tee time, and scores the round. The interface is built to carry
        all of that without feeling heavy — which, as a former Tee Time Helper subscriber, was the
        specific thing I wanted and couldn't find.
      </P>
      <P>
        Two areas are actively growing: our games and settlement tools (skins and putts formats are
        already in regular use, with tournament and outing formats in development), and season-long
        league standings (on the near-term roadmap). If those matter to your group, they're coming —
        and we're happy to hear which formats you'd want first.
      </P>

      <H2>Pricing</H2>
      <P>
        Tee Time Helper's published rate is around $9.99/month for a 20-member group, with a free
        option for smaller groups, as listed on its site as of {REVIEWED}. (Confirm current pricing
        on their site, since it can change.)
      </P>
      <P>
        Tee Time Organizer is free for up to 8 players, then scales by group size: Starter
        $4.99/month (20 players), Plus $9.99/month (40), Professional $14.99/month (100), and League
        $24.99/month (250). Because the two are priced similarly at the entry point, the real
        question isn't cost — it's whether you want scheduling alone or scheduling plus the golf
        toolkit in one place.
      </P>

      <CompareCTA
        heading="See the difference for your group"
        body="Start free in about 30 seconds — no credit card, no time limit. Keep the free plan as long as it fits, upgrade only when your group grows."
      />

      <FAQ id="tee-time-helper" faqs={FAQS} />

      <AttributionFooter competitor="Tee Time Helper" reviewed={REVIEWED} />
    </article>
  );
}
